/**
 * Thin wrapper around the browser's native WebSocket API.
 *
 * The browser WS API does NOT support setting custom HTTP headers on the
 * initial handshake — that is a known limitation. Work around it by
 * passing auth tokens as URL query parameters:
 *   wss://api.example.com/ws?token=xxx
 *
 * For desktop-only custom-header support, a follow-up Tauri command
 * (tokio-tungstenite) can be added. The hook / component API is the same.
 */
export class WebSocketClient {
  /**
   * @param {string} url - WebSocket URL (ws:// or wss://)
   * @param {(entry: {data: string, timestamp: number, direction: 'in'}) => void} onMessage
   * @param {() => void} onOpen
   * @param {(event: CloseEvent) => void} onClose
   * @param {(event: Event) => void} onError
   */
  constructor(url, onMessage, onOpen, onClose, onError) {
    this._ws = new WebSocket(url);

    this._ws.onopen = () => {
      onOpen?.();
    };

    this._ws.onmessage = (e) => {
      onMessage?.({ data: String(e.data), timestamp: Date.now(), direction: 'in' });
    };

    this._ws.onclose = (e) => {
      onClose?.(e);
    };

    this._ws.onerror = (e) => {
      onError?.(e);
    };
  }

  /** Send a text message. No-op if the socket is not open. @returns {boolean} whether it was sent */
  send(message) {
    if (this._ws.readyState !== WebSocket.OPEN) return false;
    this._ws.send(message);
    return true;
  }

  /** Cleanly close the connection. */
  close() {
    if (
      this._ws.readyState === WebSocket.OPEN ||
      this._ws.readyState === WebSocket.CONNECTING
    ) {
      this._ws.close();
    }
  }

  get readyState() {
    return this._ws.readyState;
  }
}
